import { useRef, useState } from 'react'
import { motion, useInView, AnimatePresence } from 'framer-motion'
import { ArrowRight, X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react'
import { Link } from 'react-router-dom'
import { galleryImages } from '../../data/siteData'

export default function Gallery() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })
  const [lightbox, setLightbox] = useState<number | null>(null)

  const openLightbox = (index: number) => setLightbox(index)
  const closeLightbox = () => setLightbox(null)

  const nextImage = () => {
    if (lightbox !== null) {
      setLightbox((lightbox + 1) % galleryImages.length)
    }
  }

  const prevImage = () => {
    if (lightbox !== null) {
      setLightbox((lightbox - 1 + galleryImages.length) % galleryImages.length)
    }
  }

  return (
    <section id="gallery" className="relative py-24 sm:py-32 bg-section-dark grain-overlay" style={{ backgroundColor: 'rgba(10,14,10,0.55)', backdropFilter: 'blur(2px)' }}>
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-military/40 to-transparent" />

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <span className="font-mono text-xs text-military-light tracking-[0.4em] uppercase">
            Galeria
          </span>
          <h2 className="font-display text-5xl sm:text-6xl md:text-7xl text-bone mt-4 tracking-wider">
            GALERIA EVENTÓW
          </h2>
          <p className="font-body text-chrome mt-4 max-w-2xl mx-auto">
            Każdy wyjazd to historia. Tutaj zapisujemy swoje.
          </p>
          <div className="w-24 h-0.5 bg-military-light mx-auto mt-6" />
        </motion.div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {galleryImages.map((img, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="group relative aspect-[4/3] overflow-hidden rounded-sm cursor-pointer"
              onClick={() => openLightbox(i)}
            >
              <img
                src={img}
                alt={`Event ${i + 1}`}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                loading="lazy"
              />

              {/* Overlay */}
              <div className="absolute inset-0 bg-steel/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <div className="w-14 h-14 rounded-full border-2 border-bone/60 flex items-center justify-center">
                  <ZoomIn size={24} className="text-bone" />
                </div>
              </div>

              {/* Border on hover */}
              <div className="absolute inset-0 border-2 border-military-light/0 group-hover:border-military-light/60 transition-colors duration-300 rounded-sm" />
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="text-center mt-12"
        >
          <Link
            to="/galeria"
            className="group inline-flex items-center gap-3 px-8 py-3 border border-bone/30 text-bone font-subheading font-semibold text-sm uppercase tracking-widest rounded-sm hover:bg-bone/10 hover:border-bone/60 transition-all duration-300"
          >
            Zobacz pełną galerię
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
            onClick={closeLightbox}
          >
            {/* Close */}
            <button
              onClick={closeLightbox}
              className="absolute top-6 right-6 p-2 text-chrome hover:text-bone transition-colors z-10"
            >
              <X size={32} />
            </button>

            {/* Prev */}
            <button
              onClick={(e) => { e.stopPropagation(); prevImage() }}
              className="absolute left-4 sm:left-8 p-3 text-chrome hover:text-bone transition-colors z-10"
            >
              <ChevronLeft size={40} />
            </button>

            {/* Next */}
            <button
              onClick={(e) => { e.stopPropagation(); nextImage() }}
              className="absolute right-4 sm:right-8 p-3 text-chrome hover:text-bone transition-colors z-10"
            >
              <ChevronRight size={40} />
            </button>

            {/* Image */}
            <motion.img
              key={lightbox}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.2 }}
              src={galleryImages[lightbox]}
              alt={`Event ${lightbox + 1}`}
              className="max-w-[90vw] max-h-[85vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Counter */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 font-mono text-sm text-chrome">
              {lightbox + 1} / {galleryImages.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  )
}
